<template>
  <BaseDialog centered contentClass="business-intelligence-manage-awards" ref="dialog" persistent :show-actions="true">
    <template slot="title">{{ $t('page.business-intelligence.manage-dialog.awards.title') }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <FormInput
          ref="supplierName"
          :label="$t('page.business-intelligence.manage-dialog.form.supplier-name')"
          is-required
          has-gray-background
          name="name"
          type="text"
          v-model="supplierName"
          v-validate="'required'"
          :error="supplierNameError"
        />
        <FormInput
          ref="amount"
          :label="$t('page.business-intelligence.manage-dialog.form.amount')"
          is-required
          has-gray-background
          name="amount"
          type="number"
          v-model="amount"
          v-validate="'required'"
          :error="amountError"
        />
        <div v-if="hasLots">
          <FormLabel>{{ $t('page.business-intelligence.manage-dialog.form.lot') }}</FormLabel>
          <BaseSelect name="lot" :items="lotOptions" item-text="title" item-value="id" @change="onLotChanged" />
        </div>
        <FormInput
          ref="notes"
          :label="$t('page.business-intelligence.manage-dialog.form.notes')"
          has-gray-background
          name="notes"
          type="text"
          v-model="notes"
        />
        <BaseButton has-icon-left is-full-width :disabled="isFormInvalid" @click.native="onCreate">
          <BaseIcon name="plus" />
          {{ $t('page.business-intelligence.manage-dialog.awards.create') }}
        </BaseButton>
      </form>
      <div class="__existing-winners-container">
        <div class="__subtitle">{{ $t('page.business-intelligence.manage-dialog.awards.existing') }}</div>
        <BaseLoadingBar v-if="pending" />
        <div class="__list-container">
          <template v-if="existingAwards.length > 0">
            <div class="__winner-item" v-for="award in existingAwards" :key="award.id">
              <ManualEntryRow
                :data="award"
                :reset="reset"
                :lotOptions="lotOptions"
                @on-update="onUpdate"
                @on-delete="onDelete"
              />
            </div>
          </template>
          <div v-else class="__empty">
            {{ $t('page.business-intelligence.manage-dialog.awards.empty') }}
          </div>
        </div>
      </div>
      <div class="business-intelligence-manage-awards__actions">
        <FormButton is-outlined class="business-intelligence-manage-awards__close" @click.native="closeClick">{{
          $t('actions.close')
        }}</FormButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { bus } from '@/main';
import BaseDialog from '@/components/base/BaseDialog';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BaseSelect from '@/components/base/BaseSelect';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import ManualEntryRow from './ManualEntryRow.vue';

export default {
  name: 'ManageAwardsDialog',
  components: {
    BaseDialog,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BaseSelect,
    FormButton,
    FormInput,
    FormLabel,
    ManualEntryRow,
  },
  data() {
    return {
      tenderId: null,
      supplierName: '',
      amount: 0,
      notes: '',
      relatedLot: null,
      existingAwards: [],
      hasOfficialAward: false,
      reset: false,
      hasUpdate: false,
      hasLots: false,
      lotOptions: [],
    };
  },
  computed: {
    ...mapGetters('biTenant', ['pending']),
    ...mapGetters('tender', ['tender']),
    isFormInvalid() {
      return this.errors.items.length > 0 || this.supplierName === '';
    },
    supplierNameError() {
      const formError = this.errors.items.find((x) => x.field === 'name' && !x.scope);
      return formError ? formError.msg : undefined;
    },
    amountError() {
      const formError = this.errors.items.find((x) => x.field === 'amount' && !x.scope);
      return formError ? formError.msg : undefined;
    },
  },
  methods: {
    ...mapActions('biTenant', ['createAward', 'updateAward', 'deleteAward', 'getAllAwardsByTenderIds']),
    ...mapActions('tender', ['getTender']),
    open(tenderId, hasOfficialAward) {
      this.$refs.dialog.openModal();

      this.tenderId = tenderId;
      this.hasOfficialAward = hasOfficialAward;

      this.getTender({ id: tenderId }).catch(() => {
        // already toast error, so just close the dialog
        this.close();
      });

      this.loadManualEntries();

      this.$nextTick(() => {
        const refInput = this.$refs.supplierName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    close() {
      this.$refs.dialog.isModalOpen = false;
      this.lotOptions = [];
      this.hasLots = false;

      this.errors.clear();
      this.resetRows();
      this.onRefresh();
    },
    onCreate() {
      this.hasUpdate = true;
      this.createAward({
        tenderId: this.tenderId,
        supplierName: this.supplierName,
        amount: this.amount,
        notes: this.notes,
        relatedLot: this.relatedLot,
      })
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.create-success'));
          this.loadManualEntries();
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => {
          this.resetFormState();
        });
    },
    onUpdate(id, award) {
      this.hasUpdate = true;
      this.updateAward({ id, award })
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.update-success'));
          this.loadManualEntries();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onDelete(id) {
      this.hasUpdate = true;
      this.deleteAward(id)
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.delete-success'));
          this.loadManualEntries();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onRefresh() {
      if (this.hasUpdate) bus.$emit('refresh-bi-dashboard');
    },
    onLotChanged(lot) {
      this.relatedLot = lot;
    },
    resetRows() {
      // triggers rows to reset by changing the reset prop value
      this.reset = !this.reset;
    },
    resetFormState() {
      this.supplierName = '';
      this.amount = 0;
      this.notes = '';
      this.relatedLot = null;

      this.errors.clear();
    },
    loadManualEntries() {
      this.resetRows();
      this.getAllAwardsByTenderIds([this.tenderId])
        .then((response) => {
          const data = (response[this.tenderId] || []).sort((a, b) => b.amount - a.amount);

          this.existingAwards = data || [];
          this.resetFormState();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    closeClick() {
      this.close();
    },
  },
  watch: {
    tender() {
      if (this.tender && this.tender.lots && this.tender.lots.length > 0) {
        this.hasLots = true;
        this.lotOptions = this.tender.lots;
      }
    },
  },
};
</script>

<style lang="scss">
.base-dialog__business-intelligence-manage-awards {
  height: auto;
  width: 550px;
  position: relative;

  .base-dialog__content {
    padding: 8px 32px 32px 32px !important;
    font-size: 18px;
    background: $color-white !important;

    > .__existing-winners-container {
      margin-top: 24px;

      .__subtitle {
        font-weight: 600;
        margin-bottom: 8px;
      }

      .__list-container {
        max-height: 200px;
        overflow-y: auto;
      }
    }

    .__empty {
      font-style: italic;
      font-size: $font-size-small;
      color: $color-normal-pale;
    }
  }

  .close-icon__container {
    display: none;
  }
}

.business-intelligence-manage-awards {
  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__action {
    text-transform: lowercase;
    font-weight: bold;
  }
}
</style>
